import React from 'react';
import type { FestivalEdition } from '../../types';
import { useLang } from '../../i18n/context';

interface Props {
  festival: FestivalEdition;
  onClick?: (f: FestivalEdition) => void;
  variant?: 'mega' | 'compact';
}

// The internal `kind` axis (pure_reggae / mixed / occasional) used to drive
// the visible badge. We've moved to a confidence-based UI: SEED vs VERIFIED
// (admin-confirmed) determine status colour, and detected_genre_tags from
// site validation supply the human-readable description. The kind axis stays
// in the schema for behaviour (scrape cadence, lineup filter rules) but no
// longer appears in the UI text.
const STATUS_LABELS = {
  seed:        { label: 'Seed',        fg: '#c9a84c', bg: 'rgba(201,168,76,0.12)', border: '#c9a84c' },
  verified:    { label: 'Verified',    fg: '#4caf50', bg: 'rgba(76,175,80,0.12)', border: '#4caf50' },
};

function formatDateRange(start: string, end: string, locale: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    const startDay = s.toLocaleDateString(locale, { day: 'numeric' });
    const endDayMonth = e.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startDay}–${endDayMonth}`;
  }
  const startFmt = s.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const endFmt = e.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startFmt} – ${endFmt}`;
}

function daysUntil(start: string): number {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = new Date(start);
  return Math.round((d.getTime() - now.getTime()) / 86400000);
}

export function FestivalCard({ festival, onClick, variant = 'mega' }: Props) {
  const { lang } = useLang();
  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';
  const status = festival.verified ? STATUS_LABELS.verified : STATUS_LABELS.seed;
  // Show detected genre tags inline when present (validation has run); else
  // fall back to a neutral mention of the dub stage if seeded with one.
  let detectedTags: string[] = [];
  try { detectedTags = JSON.parse(festival.detected_genre_tags || '[]'); } catch {}
  const headline = detectedTags.length > 0
    ? detectedTags.slice(0, 3).join(' · ').toUpperCase()
    : (festival.dub_stage_name ? festival.dub_stage_name : 'unverified');
  const dateRange = formatDateRange(festival.start_date, festival.end_date, locale);
  const d = daysUntil(festival.start_date);
  const countdown = d < 0 ? 'live/past' : d === 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d} days`;

  let prices: Record<string, number> | null = null;
  try {
    prices = festival.price_tier_json ? JSON.parse(festival.price_tier_json) : null;
  } catch {}

  const lineupPreview = (festival.lineup || []).slice(0, 6);

  if (variant === 'compact') {
    return (
      <div
        onClick={() => onClick?.(festival)}
        style={{
          display: 'flex', gap: '10px', padding: '10px', cursor: onClick ? 'pointer' : 'default',
          borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--card-bg)',
          alignItems: 'center',
        }}
      >
        {festival.hero_image_url ? (
          <img src={festival.hero_image_url} alt="" style={{ width: 56, height: 56, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 4, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>🎪</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{festival.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {dateRange} · {festival.city}, {festival.country}
          </div>
          <div style={{ fontSize: 10, marginTop: 3, display: 'flex', gap: 6 }}>
            <span style={{ padding: '1px 6px', borderRadius: 3, background: status.bg, color: status.fg, fontWeight: 600 }}>{status.label}</span>
            <span style={{ color: 'var(--text-muted)' }}>{countdown}</span>
          </div>
        </div>
      </div>
    );
  }

  // Mega card
  return (
    <div
      onClick={() => onClick?.(festival)}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 10, border: `2px solid ${status.border}`, overflow: 'hidden',
        background: 'var(--card-bg)', marginBottom: 14,
        transition: 'transform 0.15s ease',
      }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}
    >
      {/* Hero banner */}
      <div
        style={{
          height: 140, background: festival.hero_image_url
            ? `linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%), url(${festival.hero_image_url}) center/cover`
            : `linear-gradient(135deg, ${status.border}22, ${status.border}08)`,
          position: 'relative', display: 'flex', alignItems: 'flex-end', padding: '16px 18px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: '1px', textTransform: 'uppercase', color: status.fg, fontWeight: 700, marginBottom: 4 }}>
            {headline}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: 'white', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
            {festival.name}
          </div>
        </div>
        <div style={{
          padding: '4px 10px', borderRadius: 4,
          background: 'rgba(0,0,0,0.5)', border: `1px solid ${status.border}`,
          fontSize: 11, color: 'white', fontWeight: 700,
        }}>
          {countdown}
        </div>
      </div>

      {/* Meta row */}
      <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
        <div><span style={{ color: 'var(--text-muted)' }}>📅</span> {dateRange}</div>
        <div><span style={{ color: 'var(--text-muted)' }}>📍</span> {festival.city}{festival.venue_name ? ` · ${festival.venue_name}` : ''}, {festival.country}</div>
        {prices && (
          <div><span style={{ color: 'var(--text-muted)' }}>🎟</span> {Object.entries(prices).map(([k, v]) => `${k} ${v}€`).join(' · ')}</div>
        )}
        {festival.verified ? (
          <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓ VERIFIED</span>
        ) : (
          <span style={{ color: 'var(--gold)', fontWeight: 700 }}>⚡ SEED</span>
        )}
      </div>

      {/* Lineup preview */}
      {lineupPreview.length > 0 && (
        <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            {festival.dub_stage_name ? `${festival.dub_stage_name} lineup` : 'Lineup preview'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {lineupPreview.map(a => (
              <span key={a.id} style={{
                padding: '3px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--border-color)', fontSize: 11,
              }}>
                {a.is_sound_system ? '🔊 ' : ''}{a.artist_name}
                {a.stage_name && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>· {a.stage_name}</span>}
              </span>
            ))}
            {(festival.lineup?.length || 0) > 6 && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', padding: '3px 4px' }}>
                +{(festival.lineup!.length) - 6} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer — external links */}
      <div style={{ padding: '8px 18px', display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
        {festival.lineup_url && (
          <a href={festival.lineup_url} target="_blank" rel="noopener noreferrer"
             onClick={e => e.stopPropagation()}
             style={{ color: 'var(--gold)', textDecoration: 'none' }}>
            🔗 lineup
          </a>
        )}
        {festival.tickets_url && (
          <a href={festival.tickets_url} target="_blank" rel="noopener noreferrer"
             onClick={e => e.stopPropagation()}
             style={{ color: 'var(--gold)', textDecoration: 'none' }}>
            🎫 tickets
          </a>
        )}
        {festival.source_url && (
          <a href={festival.source_url} target="_blank" rel="noopener noreferrer"
             onClick={e => e.stopPropagation()}
             style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
            📷 @{festival.source_name}
          </a>
        )}
        {festival.notes && (
          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', marginLeft: 'auto' }}>
            {festival.notes}
          </span>
        )}
      </div>
    </div>
  );
}
